import { expireSession } from "./session";

// Client-side inactivity timeout. After 30 minutes with no user activity the
// session is expired (same flow as a 401 from the API), which clears auth and
// redirects to login.
//
// Activity is persisted to localStorage so the timer is cross-tab (activity in
// any tab keeps every tab alive) and survives a page reload.
// Idle window in minutes — keep in sync with the backend's SANCTUM_IDLE_TIMEOUT
// (config/sanctum.php). Falls back to 30 if the env var is unset/invalid.
const IDLE_MINUTES = Number(process.env.VUE_APP_SESSION_IDLE_TIMEOUT) || 30;
const TIMEOUT_MS = IDLE_MINUTES * 60 * 1000;
const CHECK_MS = 15 * 1000; // re-evaluate every 15s
const WRITE_THROTTLE_MS = 5 * 1000; // at most one localStorage write / 5s
const ACTIVITY_KEY = "worksched.lastActivity";
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "click"];

let intervalId = null;
let lastWrite = 0;
let listening = false;

function isLoggedIn() {
  return !!localStorage.getItem("jwt");
}

function markActivity() {
  if (!isLoggedIn()) return;
  const now = Date.now();
  if (now - lastWrite < WRITE_THROTTLE_MS) return; // throttle storage writes
  lastWrite = now;
  localStorage.setItem(ACTIVITY_KEY, String(now));
}

function check() {
  if (!isLoggedIn()) return;
  const last = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
  if (!last) {
    // No stamp yet (e.g. first load right after login) — start the clock now.
    resetSessionTimeout();
    return;
  }
  if (Date.now() - last >= TIMEOUT_MS) {
    expireSession();
  }
}

// Stamp activity "now" — call on successful login so the clock starts fresh.
export function resetSessionTimeout() {
  lastWrite = Date.now();
  localStorage.setItem(ACTIVITY_KEY, String(lastWrite));
}

// Drop the stamp — call on logout.
export function clearSessionTimeout() {
  localStorage.removeItem(ACTIVITY_KEY);
}

// Begin listening for activity and polling for expiry. Idempotent.
export function startSessionTimeout() {
  if (!listening) {
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true })
    );
    listening = true;
  }
  if (intervalId) clearInterval(intervalId);
  intervalId = setInterval(check, CHECK_MS);
  check(); // catch a session that already went stale while the app was closed
}
